import React, { useState } from 'react';
import { PageShell } from '../../components/Ui';
import { useHmsRecords } from '../../hooks/useHmsRecords';

/* ═══════════════════════════════════════════════════════════
   DOCUMENT MANAGEMENT SYSTEM (DMS) — NABH IMS Compliant
   - Policy & SOP Library with version control
   - Consent Forms Repository
   - Staff Training Records
   - Committee Minutes
   - Regulatory Documents (NABH, AERB, FDA)
   - Document Expiry Alerts
   - Role-based access
═══════════════════════════════════════════════════════════ */

const DOC_CATEGORIES = ['Clinical Protocols', 'SOPs', 'Policies', 'Consent Forms', 'NABH Documents', 'HR Policies', 'Committee Minutes', 'Regulatory / License', 'Training Records', 'Audit Reports', 'Forms & Templates', 'Emergency Plans'];
const MAX_FILE_BYTES = 200000;

const statusColor = { Active: '#10b981', 'Under Review': '#f59e0b', 'Expiring Soon': '#f97316', Expired: '#dc2626', Draft: '#94a3b8' };

function formatFileSize(bytes) {
  if (!bytes || bytes === '—') return '—';
  const n = Number(bytes);
  if (Number.isNaN(n)) return bytes;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function openDocument(doc) {
  if (!doc.contentBase64) {
    window.alert('No file attached — metadata only.');
    return;
  }
  const mime = doc.mimeType || 'application/pdf';
  const blob = new Blob([Uint8Array.from(atob(doc.contentBase64), (c) => c.charCodeAt(0))], { type: mime });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

function DocumentLibraryTab({ docs, onPatch }) {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [status, setStatus] = useState('All');

  const filtered = docs.filter(d => {
    const matchSearch = !search || d.title.toLowerCase().includes(search.toLowerCase()) || (d.tags || []).some(t => t.toLowerCase().includes(search.toLowerCase()));
    const matchCat = category === 'All' || d.category === category;
    const matchStatus = status === 'All' || d.status === status;
    return matchSearch && matchCat && matchStatus;
  });

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Total Documents', val: docs.length, color: '#6366f1' }, { label: 'Active', val: docs.filter(d => d.status === 'Active').length, color: '#10b981' }, { label: 'Under Review', val: docs.filter(d => d.status === 'Under Review').length, color: '#f59e0b' }, { label: 'Expiring Soon', val: docs.filter(d => d.status === 'Expiring Soon').length, color: '#f97316' }].map(s => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {docs.filter(d => d.status === 'Expiring Soon' || d.status === 'Under Review').length > 0 && (
        <div style={{ padding: '12px 16px', background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: 10, marginBottom: 14, fontSize: 13, color: '#92400e' }}>
          ⚠ <strong>{docs.filter(d => d.status === 'Expiring Soon').length}</strong> document(s) expiring soon · <strong>{docs.filter(d => d.status === 'Under Review').length}</strong> under review — action required for NABH compliance
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <input className="input" style={{ flex: 1, minWidth: 200 }} placeholder="Search title, tags..." value={search} onChange={e => setSearch(e.target.value)} />
        <select className="input" style={{ width: 'auto' }} value={category} onChange={e => setCategory(e.target.value)}>
          <option value="All">All Categories</option>
          {DOC_CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
        <select className="input" style={{ width: 'auto' }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="All">All Status</option>
          {['Active', 'Under Review', 'Expiring Soon', 'Expired', 'Draft'].map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 10 }}>
        {filtered.map(doc => (
          <div key={doc.id} className="card" style={{ borderLeft: `4px solid ${statusColor[doc.status] || '#94a3b8'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, gap: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: 13 }}>{doc.title}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{doc.category} · {doc.version}</div>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: (statusColor[doc.status] || '#94a3b8') + '18', color: statusColor[doc.status] || '#94a3b8', border: `1px solid ${(statusColor[doc.status] || '#94a3b8')}40`, height: 'fit-content', whiteSpace: 'nowrap' }}>{doc.status}</span>
            </div>
            <div style={{ fontSize: 11, color: '#475569', marginBottom: 4 }}>📁 {doc.dept}</div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>By: {doc.author} · {formatFileSize(doc.fileSize)}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>Review Date: <strong style={{ color: doc.status === 'Expiring Soon' ? '#f97316' : '#475569' }}>{doc.reviewDate}</strong></div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
              {(doc.tags || []).map(tag => <span key={tag} style={{ fontSize: 10, padding: '1px 6px', background: '#eff6ff', color: '#2563eb', borderRadius: 999, fontWeight: 600, border: '1px solid #bfdbfe' }}>{tag}</span>)}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" className="btn btn-primary btn-sm" style={{ fontSize: 10 }} onClick={() => openDocument(doc)}>📄 View</button>
              <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 10 }}>🔄 New Version</button>
              {doc.status === 'Under Review' && <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 10, background: '#f0fdf4', color: '#16a34a', border: '1px solid #86efac' }} onClick={() => onPatch?.(doc.recordId || doc.id, { status: 'Active' })}>✓ Approve</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UploadDocTab({ onSave }) {
  const [form, setForm] = useState({ title: '', category: DOC_CATEGORIES[0], version: 'v1.0', dept: '', author: '', reviewDate: '', tags: '', notes: '' });
  const [file, setFile] = useState(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  return (
    <form className="card" style={{ borderLeft: '4px solid #6366f1' }} onSubmit={async (e) => {
      e.preventDefault();
      if (!onSave) return;
      setBusy(true);
      setErr('');
      try {
        let contentBase64;
        let fileName;
        let mimeType;
        let fileSize = '—';
        if (file) {
          if (file.size > MAX_FILE_BYTES) {
            throw new Error(`File too large (max ${Math.round(MAX_FILE_BYTES / 1024)} KB). Use a smaller PDF or split the document.`);
          }
          contentBase64 = await readFileAsBase64(file);
          fileName = file.name;
          mimeType = file.type || 'application/octet-stream';
          fileSize = file.size;
        }
        await onSave({
          ...form,
          id: `DOC-${Date.now()}`,
          tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
          createdAt: new Date().toISOString().slice(0, 10),
          status: 'Active',
          fileSize,
          fileName,
          mimeType,
          contentBase64: contentBase64 || undefined,
          approvedBy: null,
        });
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
        setForm({ title: '', category: DOC_CATEGORIES[0], version: 'v1.0', dept: '', author: '', reviewDate: '', tags: '', notes: '' });
        setFile(null);
      } catch (ex) {
        setErr(ex.message || 'Upload failed');
      } finally { setBusy(false); }
    }}>
      <h3 style={{ marginTop: 0, color: '#6366f1' }}>📤 Upload / Add Document</h3>
      {saved && <div className="info-banner mb">✅ Document added to library!</div>}
      {err && <div className="error mb">{err}</div>}
      <div className="form-grid mb">
        <div className="field full"><label>Document Title *</label><input className="input" required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Hand Hygiene Policy" /></div>
        <div className="field"><label>Category *</label><select className="input" required value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>{DOC_CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></div>
        <div className="field"><label>Version</label><input className="input" value={form.version} onChange={e => setForm(f => ({ ...f, version: e.target.value }))} placeholder="v1.0" /></div>
        <div className="field"><label>Department</label><input className="input" value={form.dept} onChange={e => setForm(f => ({ ...f, dept: e.target.value }))} placeholder="e.g. All / ICU / Nursing" /></div>
        <div className="field"><label>Author / Owner</label><input className="input" value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))} /></div>
        <div className="field"><label>Next Review Date *</label><input type="date" className="input" required value={form.reviewDate} onChange={e => setForm(f => ({ ...f, reviewDate: e.target.value }))} /></div>
        <div className="field full"><label>Tags (comma-separated)</label><input className="input" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="NABH, HIC, Nursing..." /></div>
        <div className="field full">
          <label>Document File (Upload)</label>
          <input type="file" className="input" accept=".pdf,.doc,.docx,.xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          {file && <div className="hint" style={{ marginTop: 4 }}>{file.name} · {formatFileSize(file.size)}</div>}
        </div>
        <div className="field full"><label>Notes</label><textarea className="input" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
      </div>
      <div className="form-actions"><button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Uploading…' : '📤 Upload Document'}</button></div>
    </form>
  );
}

export default function DocumentManagementPage() {
  const { records: docs, create, patch } = useHmsRecords('hospital-document');
  const [tab, setTab] = useState('library');

  return (
    <PageShell title="Document Management System (DMS)" subtitle="Policies · SOPs · Consent Forms · NABH Documents · Version Control — NABH IMS Compliant"
      actions={<button type="button" className="btn btn-primary" onClick={() => setTab('upload')}>+ Upload Document</button>}>
      <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
        {[{ id: 'library', label: '📚 Document Library' }, { id: 'upload', label: '📤 Upload Document' }].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>
      {tab === 'library' && <DocumentLibraryTab docs={docs} onPatch={patch} />}
      {tab === 'upload' && <UploadDocTab onSave={create} />}
    </PageShell>
  );
}
